import { BimCategory, BimCluster, BimGroup, BimObject } from '../models/Bim'
import { isUUID } from './Common'

const groupBimObjects = (objects: BimObject[]): BimCluster[] => {
  const clusterMap = new Map<BimCategory, Map<string, string[]>>()

  for (const obj of objects) {
    const category = obj.category
    const subCategory = obj.subCategory

    if (!clusterMap.has(category)) {
      clusterMap.set(category, new Map())
    }

    const subMap = clusterMap.get(category)!
    if (!subMap.has(subCategory)) {
      subMap.set(subCategory, [])
    }

    subMap.get(subCategory)!.push(obj.id)
  }

  const clusters: BimCluster[] = []

  clusterMap.forEach((subMap, category) => {
    const subcategories: BimGroup[] = []

    subMap.forEach((objectIds, name) => {
      subcategories.push({ name, objectIds })
    })

    subcategories.sort((a, b) => a.name.localeCompare(b.name))
    clusters.push({ category, subcategories })
  })

  const allCategories = Object.values(BimCategory)
  for (const category of allCategories) {
    if (!clusterMap.has(category)) {
      clusterMap.set(category, new Map())
    }
  }

  return clusters
}

const getBimObjectRefId = (
  data: any,
  id: string,
): { id: string; bimobject_ref: string }[] | undefined => {
  const results: { id: string; bimobject_ref: string }[] = []

  const collectRefs = (obj: any) => {
    if (
      obj &&
      typeof obj === 'object' &&
      'id' in obj &&
      'bimobject_ref' in obj &&
      isUUID(obj.bimobject_ref)
    ) {
      results.push({ id: obj.id, bimobject_ref: obj.bimobject_ref })
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key]
        if (typeof value === 'object') {
          collectRefs(value)
        }
      }
    }

    if (Array.isArray(obj)) {
      for (const item of obj) {
        collectRefs(item)
      }
    }
  }

  const findAndCollect = (obj: any): boolean => {
    if (obj && typeof obj === 'object') {
      if (obj.id === id) {
        collectRefs(obj)
        return true
      }

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (findAndCollect(obj[key])) return true
        }
      }
    }

    if (Array.isArray(obj)) {
      for (const item of obj) {
        if (findAndCollect(item)) return true
      }
    }

    return false
  }

  const found = findAndCollect(data)
  return found ? results : undefined
}

const collectBimObjectRefIds = (data: any, refIds = new Set<string>()) => {
  if (data && typeof data === 'object') {
    if (
      'bimobject_ref' in data &&
      data.bimobject_ref &&
      isUUID(data.bimobject_ref)
    ) {
      refIds.add(data.bimobject_ref)
    }

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        collectBimObjectRefIds(data[key], refIds)
      }
    }

    if (Array.isArray(data)) {
      for (const item of data) {
        collectBimObjectRefIds(item, refIds)
      }
    }
  }

  return refIds
}

const buildBimObjectRefIdMap = (
  data: any,
  map = new Map(),
): Map<string, Set<string>> => {
  if (data && typeof data === 'object') {
    if ('bimobject_ref' in data && 'id' in data && isUUID(data.bimobject_ref)) {
      if (!map.has(data.bimobject_ref)) {
        map.set(data.bimobject_ref, new Set())
      }
      map.get(data.bimobject_ref).add(data.id)
    }

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        buildBimObjectRefIdMap(data[key], map)
      }
    }

    if (Array.isArray(data)) {
      for (const item of data) {
        buildBimObjectRefIdMap(item, map)
      }
    }
  }

  return map
}

export {
  groupBimObjects,
  getBimObjectRefId,
  collectBimObjectRefIds,
  buildBimObjectRefIdMap,
}
